import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Tooltip.module.css';
export function Tooltip({ children, content, position = 'top', trigger = 'hover', dataTestId = 'tooltip', className = '', disabled = false, }) {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);
    const [lastTooltipDimensions, setLastTooltipDimensions] = useState({ width: 0, height: 0 });
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const attemptCountRef = useRef(0);
    const hideTimeoutRef = useRef(null);
    const isHoveringRef = useRef(false);
    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current)
            return;
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 8;
        if (tooltipRect.width === 0 || tooltipRect.height === 0) {
            if (attemptCountRef.current >= 3) {
                let fallbackTop = 0;
                let fallbackLeft = 0;
                switch (position) {
                    case 'top':
                        fallbackTop = triggerRect.top - 40 - margin;
                        fallbackLeft = triggerRect.left + triggerRect.width / 2 - 100;
                        break;
                    case 'bottom':
                        fallbackTop = triggerRect.bottom + margin;
                        fallbackLeft = triggerRect.left + triggerRect.width / 2 - 100;
                        break;
                    case 'left':
                        fallbackTop = triggerRect.top + triggerRect.height / 2 - 20;
                        fallbackLeft = triggerRect.left - 200 - margin;
                        break;
                    case 'right':
                        fallbackTop = triggerRect.top + triggerRect.height / 2 - 20;
                        fallbackLeft = triggerRect.right + margin;
                        break;
                }
                setTooltipPosition({ top: fallbackTop, left: fallbackLeft });
                setIsPositioned(true);
                return;
            }
            let initialTop = 0;
            let initialLeft = 0;
            switch (position) {
                case 'top':
                    initialTop = triggerRect.top - 50 - margin;
                    initialLeft = triggerRect.left + triggerRect.width / 2 - 100;
                    break;
                case 'bottom':
                    initialTop = triggerRect.bottom + margin;
                    initialLeft = triggerRect.left + triggerRect.width / 2 - 100;
                    break;
                case 'left':
                    initialTop = triggerRect.top + triggerRect.height / 2 - 25;
                    initialLeft = triggerRect.left - 200 - margin;
                    break;
                case 'right':
                    initialTop = triggerRect.top + triggerRect.height / 2 - 25;
                    initialLeft = triggerRect.right + margin;
                    break;
            }
            setTooltipPosition({ top: initialTop, left: initialLeft });
            setIsPositioned(false);
            attemptCountRef.current += 1;
            if (attemptCountRef.current < 3) {
                setTimeout(() => calculatePosition(), 0);
            }
            return;
        }
        let top = 0;
        let left = 0;
        switch (position) {
            case 'top':
                top = triggerRect.top - tooltipRect.height - margin;
                left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
                break;
            case 'bottom':
                top = triggerRect.bottom + margin;
                left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
                break;
            case 'left':
                top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
                left = triggerRect.left - tooltipRect.width - margin;
                break;
            case 'right':
                top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
                left = triggerRect.right + margin;
                break;
        }
        const minLeft = margin;
        const maxLeft = viewportWidth - margin;
        const minTop = margin;
        const maxTop = viewportHeight - margin;
        left = Math.max(minLeft, Math.min(left, maxLeft - 50));
        top = Math.max(minTop, Math.min(top, maxTop - 30));
        setTooltipPosition({ top, left });
        setIsPositioned(true);
        setLastTooltipDimensions({ width: tooltipRect.width, height: tooltipRect.height });
    }, [position]);
    const showTooltip = () => {
        if (disabled)
            return;
        isHoveringRef.current = true;
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        setIsVisible(true);
        setIsPositioned(false);
        attemptCountRef.current = 0;
        setLastTooltipDimensions({ width: 0, height: 0 });
    };
    const cancelHide = () => {
        isHoveringRef.current = true;
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };
    const hideTooltip = () => {
        isHoveringRef.current = false;
        if (hideTimeoutRef.current)
            clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
            if (!isHoveringRef.current) {
                setIsVisible(false);
                setIsPositioned(false);
                attemptCountRef.current = 0;
                setLastTooltipDimensions({ width: 0, height: 0 });
            }
        }, 100);
    };
    const handleClick = () => {
        if (disabled)
            return;
        if (trigger === 'click') {
            setIsVisible(!isVisible);
        }
    };
    useEffect(() => {
        if (isVisible) {
            calculatePosition();
            const handleResize = () => {
                if (isVisible) {
                    calculatePosition();
                }
            };
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isVisible, position, calculatePosition]);
    useEffect(() => {
        if (!isVisible || !isPositioned || !tooltipRef.current)
            return;
        const checkDimensions = () => {
            if (!tooltipRef.current)
                return;
            const rect = tooltipRef.current.getBoundingClientRect();
            const currentDimensions = { width: rect.width, height: rect.height };
            if (Math.abs(currentDimensions.height - lastTooltipDimensions.height) > 1 ||
                Math.abs(currentDimensions.width - lastTooltipDimensions.width) > 1) {
                calculatePosition();
            }
        };
        const timeoutId = setTimeout(checkDimensions, 50);
        return () => clearTimeout(timeoutId);
    }, [isVisible, isPositioned, tooltipPosition, lastTooltipDimensions, calculatePosition]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            const targetElement = target instanceof Element ? target : null;
            if (targetElement?.closest('[data-ibk-outside-click-ignore="true"]'))
                return;
            if (trigger === 'click' &&
                isVisible &&
                triggerRef.current &&
                target &&
                !triggerRef.current.contains(target) &&
                tooltipRef.current &&
                !tooltipRef.current.contains(target)) {
                hideTooltip();
            }
        };
        if (trigger === 'click') {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [trigger, isVisible]);
    const triggerProps = {
        ref: triggerRef,
        onClick: handleClick,
        ...(trigger === 'hover' && {
            onMouseEnter: showTooltip,
            onMouseLeave: hideTooltip,
        }),
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { ...triggerProps, className: [styles.trigger, className].join(' '), "data-testid": `${dataTestId}Trigger`, children: children }), isVisible && (_jsxs("div", { ref: tooltipRef, className: [styles.tooltip, styles[position]].join(' '), style: {
                    position: 'fixed',
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    maxWidth: isPositioned
                        ? `${Math.max(120, Math.min(320, window.innerWidth - tooltipPosition.left - 16))}px`
                        : '320px',
                    opacity: isPositioned ? 1 : 0.01,
                    visibility: 'visible',
                    zIndex: 9999,
                    pointerEvents: 'none',
                }, onMouseEnter: cancelHide, onMouseLeave: hideTooltip, "data-testid": `${dataTestId}Content`, role: 'tooltip', "aria-hidden": !isVisible, children: [_jsx("div", { className: styles.content, children: content }), _jsx("div", { className: styles.arrow })] }))] }));
}
